import React, { useMemo, useState } from 'react';
import {
  AddOn,
  Appointment,
  Company,
  Customer,
  QuestionnaireSubmission,
  Service,
  Venue
} from '@/types';
import { ManualBookingModal } from '../manualBooking/ManualBookingModal';
import { BookingList } from './BookingList';
import { BookingEditor } from './BookingEditor';
import { ReminderModal } from './ReminderModal';
import { SageConfirmDialog, SageJobPanel, useSageSync } from './SageSync';
import { useAppointmentMutations } from '@features/admin/data';
import { buildMapById } from '@/lib/collections';
import { showConfirmModal, showAlertModal } from '@shared/components/common/AlertModal';
import { showToast } from '@shared/components/common/Toast';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface BookingsTabProps {
  services: Service[];
  venues: Venue[];
  customers: Customer[];
  companies: Company[];
  addOns: AddOn[];
  submissions: QuestionnaireSubmission[];
  onViewCustomer: (customerId: string) => void;
  onViewCustomerSearch: (query: string) => void;
  onForceRefresh?: () => void;
  canSyncToSage?: boolean;
}

export const BookingsTab: React.FC<BookingsTabProps> = ({
  services,
  venues,
  customers,
  companies,
  addOns,
  onViewCustomer,
  onViewCustomerSearch,
  onForceRefresh,
  canSyncToSage = false
}) => {
  const appointmentMutations = useAppointmentMutations();

  const handleSendQuestionnaire = async (id: string, alreadySubmitted: boolean) => {
    const confirmed = await showConfirmModal(
      alreadySubmitted
        ? 'This questionnaire was already submitted — re-send the link to the customer anyway?'
        : 'Send the questionnaire link to this customer by email?',
      {
        title: 'Send Questionnaire',
        confirmText: 'Send',
        cancelText: 'Cancel',
        isDestructive: false
      }
    );
    if (!confirmed) return;
    try {
      await appointmentMutations.sendQuestionnaire(id);
      showToast('Questionnaire sent.', { type: 'success' });
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to send questionnaire.'));
    }
  };

  const [editingBooking, setEditingBooking] = useState<Appointment | null>(null);
  const [reminderModal, setReminderModal] = useState<{ id: string; type: 'sms' | 'email' } | null>(
    null
  );
  const [isManualBookingOpen, setIsManualBookingOpen] = useState(false);
  const [pageAppointments, setPageAppointments] = useState<Appointment[]>([]);
  const [selectedBookingIds, setSelectedBookingIds] = useState<Set<string>>(new Set());

  const companiesById = useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies]);

  const selectedAppointments = useMemo(
    () => pageAppointments.filter((a) => selectedBookingIds.has(a.id)),
    [pageAppointments, selectedBookingIds]
  );

  const sage = useSageSync({
    selectedBookingIds,
    selectedAppointments,
    companiesById,
    onClearSelection: () => setSelectedBookingIds(new Set()),
    onForceRefresh
  });

  const handleAddBooking = async (booking: Appointment) => {
    // Let errors propagate so ManualBookingModal can surface a payment-aware
    // failure message (and keep itself open) instead of a disconnected alert.
    await appointmentMutations.create(booking);
  };

  const handlePageLoaded = (items: Appointment[]) => {
    setPageAppointments(items);
    // Reset selection when page content changes
    setSelectedBookingIds((prev) => {
      const ids = new Set(items.map((a) => a.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  };

  const handleToggleSelection = (id: string, checked: boolean) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleToggleSelectAll = (checked: boolean) => {
    setSelectedBookingIds(checked ? new Set(pageAppointments.map((a) => a.id)) : new Set());
  };

  const servicesById = useMemo(() => buildMapById(services), [services]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);

  return (
    <>
      {isManualBookingOpen && (
        <ManualBookingModal
          onClose={() => setIsManualBookingOpen(false)}
          onSave={handleAddBooking}
          services={services}
          venues={venues}
          addOns={addOns}
          servicesById={servicesById}
          venuesById={venuesById}
          addOnsById={addOnsById}
          customers={customers}
          companies={companies}
          appointments={pageAppointments}
        />
      )}

      {reminderModal && (
        <ReminderModal
          appointmentId={reminderModal.id}
          type={reminderModal.type}
          onClose={() => setReminderModal(null)}
        />
      )}

      {sage.confirmOpen && (
        <SageConfirmDialog
          selectedCount={selectedBookingIds.size}
          selectionGroups={sage.selectionGroups}
          onConfirm={sage.handleQueue}
          onClose={sage.closeConfirm}
        />
      )}

      {sage.jobPanelOpen && sage.jobId && (
        <SageJobPanel
          jobId={sage.jobId}
          onClose={sage.closeJobPanel}
          onJobComplete={sage.handleJobComplete}
        />
      )}

      {editingBooking && (
        <BookingEditor
          booking={editingBooking}
          venues={venues}
          services={services}
          addOns={addOns}
          companies={companies}
          onClose={() => setEditingBooking(null)}
          onSaved={() => setEditingBooking(null)}
          onDeleted={() => setEditingBooking(null)}
        />
      )}

      <BookingList
        services={services}
        venues={venues}
        customers={customers}
        companies={companies}
        addOns={addOns}
        canSyncToSage={canSyncToSage}
        sageJobId={sage.jobId}
        selectedBookingIds={selectedBookingIds}
        onPageLoaded={handlePageLoaded}
        onEdit={setEditingBooking}
        onSendSms={(id) => setReminderModal({ id, type: 'sms' })}
        onSendEmail={(id) => setReminderModal({ id, type: 'email' })}
        onSendQuestionnaire={handleSendQuestionnaire}
        onViewCustomer={onViewCustomer}
        onViewCustomerSearch={onViewCustomerSearch}
        onToggleSelection={handleToggleSelection}
        onToggleSelectAll={handleToggleSelectAll}
        onOpenSageConfirm={sage.openConfirm}
        onOpenSageJob={sage.openJobPanel}
        onNewBooking={() => setIsManualBookingOpen(true)}
      />
    </>
  );
};
